import { useState, useEffect, useRef } from 'react';
import { ExternalLink, Heart, Repeat2, MessageCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LatestTweet = () => {
  const [tweets, setTweets] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false); // Start hidden, will become visible
  const [isFading, setIsFading] = useState(false);
  const sectionRef = useRef(null);

  // Fetch tweets on mount
  useEffect(() => {
    const fetchTweets = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/compose/public/recent`);
        if (res.ok) {
          const data = await res.json();
          if (data.tweets && data.tweets.length > 0) {
            setTweets(data.tweets);
          }
        }
      } catch (error) {
        console.error('Failed to fetch tweets:', error);
      }
    };

    fetchTweets();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTweets, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate tweets every 8 seconds
  useEffect(() => {
    if (tweets.length <= 1) return;

    const rotateInterval = setInterval(() => {
      setIsFading(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % tweets.length);
        setIsFading(false);
      }, 500); // Half of transition duration
    }, 8000);

    return () => clearInterval(rotateInterval);
  }, [tweets.length]);

  // Intersection observer for scroll animation  
  useEffect(() => {
    // Set visible immediately if not already (in case intersection observer doesn't trigger)
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }  // Lower threshold for easier triggering
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  // Don't render if no tweets
  if (tweets.length === 0) {
    return null;
  }

  const currentTweet = tweets[currentIndex];
  const engagement = currentTweet?.engagement || {};

  // Format relative time
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <section
      ref={sectionRef}
      className={`py-16 px-6 transform transition-all duration-700 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}
    >
      <div className="max-w-2xl mx-auto">
        {/* Label */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-[#00F0FF]/30" />
          <span className="font-mono text-xs tracking-[0.2em] text-[#6B7280] uppercase">
            Latest Take
          </span>
          <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-[#00F0FF]/30" />
        </div>

        {/* Tweet Card */}
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute -inset-1 bg-gradient-to-r from-[#00F0FF]/10 via-transparent to-[#00F0FF]/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <div className="relative p-6 rounded-xl bg-white/[0.02] border border-white/10 hover:border-[#00F0FF]/30 transition-all duration-300">
            {/* Tweet text with fade transition */}
            <p
              className={`font-sans text-lg text-[#F3F4F6] leading-relaxed text-center transition-opacity duration-500 ${
                isFading ? 'opacity-0' : 'opacity-100'
              }`}
            >
              &ldquo;{currentTweet?.text}&rdquo;
            </p>

            {/* Footer */}
            <div
              className={`flex items-center justify-center gap-6 mt-6 pt-4 border-t border-white/5 transition-opacity duration-500 ${
                isFading ? 'opacity-0' : 'opacity-100'
              }`}
            >
              {/* Engagement stats */}
              <div className="flex items-center gap-4 text-[#6B7280]">
                <span className="flex items-center gap-1 text-sm">
                  <Heart className="w-4 h-4" />
                  {engagement.likes || 0}
                </span>
                <span className="flex items-center gap-1 text-sm">
                  <Repeat2 className="w-4 h-4" />
                  {engagement.retweets || 0}
                </span>
                {engagement.replies > 0 && (
                  <span className="flex items-center gap-1 text-sm">
                    <MessageCircle className="w-4 h-4" />
                    {engagement.replies}
                  </span>
                )}
              </div>

              {/* Divider */}
              <span className="text-[#6B7280]/30">•</span>

              {/* Time */}
              <span className="text-sm text-[#6B7280]">
                {formatTime(currentTweet?.posted_at)}
              </span>

              {/* Divider */}
              <span className="text-[#6B7280]/30">•</span>

              {/* Link to X */}
              {currentTweet?.tweet_url && (
                <a
                  href={currentTweet.tweet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-[#00F0FF] hover:text-[#00F0FF]/80 transition-colors"
                >
                  View on X
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {/* Progress dots */}
            {tweets.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                {tweets.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setIsFading(true);
                      setTimeout(() => {
                        setCurrentIndex(idx);
                        setIsFading(false);
                      }, 300);
                    }}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentIndex
                        ? 'bg-[#00F0FF] w-4'
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestTweet;
